"use client";

import { useEffect, useRef, useState } from "react";
import { APP_FRAME_SCROLL_EVENT, type AppFrameScrollDetail } from "@/lib/events";

// Initial placeholder for the spacer height before ResizeObserver fires.
// The real value comes from measuring the Nav's actual rendered height —
// see the effect below. Hardcoding a constant produced a ~4px gap on mobile
// (Nav rendered shorter than the constant) and ~4px overlap on tablet/
// desktop (taller). Measurement removes the breakpoint-dependence entirely.
const NAV_H_FALLBACK = 78;

export default function NavScrollWrapper({ children, appFrame = false }: { children: React.ReactNode; appFrame?: boolean }) {
  const [hidden, setHidden] = useState(false);
  const [navH, setNavH] = useState(NAV_H_FALLBACK);
  const lastY = useRef(0);
  /** Set while the app-frame spacer animates — see `handle`. */
  const suppressUntil = useRef(0);
  const hiddenRef = useRef(false);
  const accum = useRef(0);
  const navRef = useRef<HTMLDivElement>(null);

  // Measure the Nav's actual rendered height and keep the spacer + the
  // --sticky-top CSS variable in sync. ResizeObserver fires on initial
  // layout, font load, breakpoint changes, and any future Nav content
  // changes — the gap can't reappear without the observer also firing.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;

    const apply = (h: number) => {
      setNavH(h);
      document.documentElement.style.setProperty(
        "--sticky-top",
        hiddenRef.current ? "0px" : `${h}px`,
      );
      // ⭐ `--nav-height` is the SAME measurement WITHOUT the hide-on-scroll
      // collapse, and the two answer different questions. `--sticky-top` means
      // "how much chrome is covering the top RIGHT NOW" — right for anything
      // that must sit under the bar as it comes and goes. `--nav-height` means
      // "how tall is the bar when it is shown", which is what an anchor jump
      // needs, because the nav's state at the END of the jump is always the
      // OPPOSITE of the one at the start (measured 2026-09-08: jumping DOWN
      // starts with it shown and ends with it hidden, jumping UP the reverse),
      // so a scroll offset read from `--sticky-top` is wrong in both
      // directions — and the upward one HID the heading behind the nav.
      // It is never set to 0, so it stays a stable target for `scroll-margin`.
      document.documentElement.style.setProperty("--nav-height", `${h}px`);
    };

    const remeasure = () => {
      if (navRef.current) apply(navRef.current.getBoundingClientRect().height);
    };

    apply(el.getBoundingClientRect().height);

    // ⛔ ONE MEASUREMENT AT EFFECT TIME IS NOT ENOUGH, AND THE OBSERVER CANNOT
    // SAVE YOU — a ResizeObserver fires when the box CHANGES, so a reading
    // taken before the sub-bar has its final height is never corrected: the
    // element was already its final size and simply never resizes again.
    // Measured on `/chat` 2026-09-05: the wrapper is 114px (DemoBanner 36 +
    // header 78) while the spacer stayed at the 78px reading, so the top 36px
    // of the frame's content sat behind the Nav, permanently and silently. It
    // did not reproduce on every route or every load, which is exactly what an
    // early-read race looks like.
    //
    // So re-measure at the two moments a header's height is known to settle
    // late. Both are idempotent — `apply` just writes the same number again
    // when nothing moved — and `setNavH` bails out of a re-render on an equal
    // value, so the cost of the extra reads is one layout each.
    const raf = requestAnimationFrame(remeasure);
    // Fonts are the usual culprit: the nav is Montserrat + Inter with
    // `display: swap`, so the fallback face can measure a different height.
    let cancelled = false;
    document.fonts?.ready.then(() => { if (!cancelled) remeasure(); });

    // ResizeObserver `entry.contentRect` reports the CONTENT box (excludes
    // padding + border) regardless of box-sizing — using it would shrink
    // the spacer to ~31px on mobile, hiding ~40px of next-element under
    // the fixed Nav. getBoundingClientRect() returns the border-box size,
    // matching the actual rendered footprint. The extra layout read is
    // cheap; ResizeObserver fires at most once per layout pass.
    const ro = new ResizeObserver(remeasure);
    ro.observe(el);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      // The variable lives on <html>, which outlives this Nav: without this,
      // a client-side move to a page with no Nav (login, signup) kept the old
      // height, and a fixed strip hung that far down over an empty band
      // (measured 2026-09-26: 81px). A Nav on the next page republishes it in
      // its own mount effect, which runs after this cleanup.
      document.documentElement.style.removeProperty("--sticky-top");
    };
  }, []);

  useEffect(() => {
    // Takes the position rather than reading `window`, so an app-frame page
    // whose inner panel scrolls (today `/chat`, where the document never does)
    // can drive the same hide/show with the same jitter handling.
    const handle = (y: number) => {
      // Our own layout change is not a user scroll. Collapsing the app-frame
      // spacer makes the inner scroller TALLER, which clamps its scrollTop and
      // emits a scroll event with a downward-looking delta — that re-showed the
      // Nav, which re-expanded the spacer, which clamped again: a visible
      // oscillation instead of a hide (measured 2026-08-22). The retired
      // `BottomNav` carried the same guard for the document-height version.
      // `lastY` is still tracked through the window so the first real scroll
      // afterwards has a sane delta rather than a jump.
      if (Date.now() < suppressUntil.current) { lastY.current = y; return; }
      const delta = y - lastY.current;
      lastY.current = y;

      // Accumulate small deltas in the same direction; reset on direction change
      if ((accum.current > 0 && delta < 0) || (accum.current < 0 && delta > 0)) {
        accum.current = 0;
      }
      accum.current += delta;

      let next = hiddenRef.current;
      if (y < navH) {
        next = false;
        accum.current = 0;
      } else if (accum.current > 10) {
        next = true;
      } else if (accum.current < -10) {
        next = false;
      }

      if (next !== hiddenRef.current) {
        hiddenRef.current = next;
        accum.current = 0;
        // Cover the 300ms height transition plus a frame of settling.
        if (appFrame) suppressUntil.current = Date.now() + 400;
        setHidden(next);
        document.documentElement.style.setProperty(
          "--sticky-top",
          next ? "0px" : `${navH}px`,
        );
      }
    };

    const onScroll = () => handle(window.scrollY);
    // The FIRST app-frame event is a position report, not a scroll: ChatClient
    // pins a resumed thread to the bottom with its dispatch suppressed, so the
    // first event this listener ever sees carries y ≈ the full scroll height —
    // read as a delta from lastY's initial 0, that "scrolled down thousands of
    // px" hid the Nav on the user's first UPWARD flick (review 2026-08-22).
    // Seed from it instead of reacting to it.
    let frameSeeded = false;
    const onFrame = (e: Event) => {
      const { y, reset } = (e as CustomEvent<AppFrameScrollDetail>).detail;
      // The scroller went away (the chat's empty state, which cannot scroll):
      // show the nav, and UN-seed, so the next thread's first report is again
      // a position, not a scroll. Handling it as y=0 instead consumed the seed,
      // and the first flick in every new conversation hid the nav, upward
      // flicks included (review 2026-09-19, measured).
      if (reset) {
        frameSeeded = false;
        accum.current = 0;
        if (hiddenRef.current) {
          hiddenRef.current = false;
          setHidden(false);
          document.documentElement.style.setProperty("--sticky-top", `${navH}px`);
        }
        return;
      }
      if (!frameSeeded) { frameSeeded = true; lastY.current = y; return; }
      handle(y);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener(APP_FRAME_SCROLL_EVENT, onFrame);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener(APP_FRAME_SCROLL_EVENT, onFrame);
    };
  }, [navH, appFrame]);

  return (
    <>
      <div
        ref={navRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          transform: hidden ? "translateY(-100%)" : "translateY(0)",
          transition: "transform 300ms ease",
        }}
      >
        {children}
      </div>
      {/* Spacer matches the measured Nav height — prevents content from
          slipping behind the fixed Nav and avoids the gap that appears
          when a hardcoded constant doesn't match actual breakpoint sizing.

          `appFrame` collapses it while the Nav is hidden, and is opt-in for a
          reason: on a normal page the DOCUMENT scrolls, so removing 80px from
          the top would yank everything up under the reader's eyes. An app
          frame (`/chat`) never scrolls its document — an inner panel does — so
          there is nothing to jump, and leaving the spacer at full height just
          left an empty band where the Nav used to be (owner report
          2026-08-22). Same duration/easing as the Nav's own transform so the
          two move together. */}
      <div
        style={{
          height: appFrame && hidden ? 0 : navH,
          transition: appFrame ? "height 300ms ease" : undefined,
          // ⛔ `height` IS NOT A FLOOR IN A FLEX COLUMN. On an app frame this
          // spacer is a flex child of a fixed-height `flex-col` box, so the
          // default `flex-shrink: 1` lets it be squeezed below its own height
          // as soon as the siblings need room — measured 2026-09-05: the inline
          // cookie banner appeared and the spacer rendered 78px while its
          // inline style said 114px, putting the frame's content back under the
          // Nav. Inert on an ordinary page, where it is not a flex item.
          flexShrink: 0,
        }}
        aria-hidden="true"
      />
    </>
  );
}
